import { gql, useEnhancedMutation } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import { notEmpty } from "@uplift-ltd/ts-helpers";
import { useFormikContext } from "formik";
import groupBy from "lodash/groupBy";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import { AnswerTypeEnum, CourseProgressEnum, QuizAnswerInput } from "__generated__/globalTypes";
import { ButtonLink } from "components/common/Button";
import {
  EnhancedFormik,
  Form,
  FormStatus,
  InputGroupField,
  SubmitButton,
} from "components/common/Form";
import { PageTitle } from "components/common/Page";
import { COURSE_URL } from "constants/urls";
import { getQuizTitleAndNumber } from "helpers/courseProgress";
import useRouterNavigation from "hooks/useRouterNavigation";
import { useRouterQuery } from "hooks/useRouterQuery";
import { CourseViewUserQueryCourse } from "../__generated__/queries";
import {
  COMMENTS_ESSAY_INPUT_NAME,
  CONFIRMATION_INPUT_NAME,
  CourseQuiz,
  FormValues,
  QuestionTypes,
} from "../types";
import QuizQuestion from "./QuizQuestion";
import { SubmitQuizAnswersMutation, SubmitQuizAnswersVariables } from "./__generated__/Quiz";
import styles from "./Quiz.module.css";

const INCOMPLETE_QUIZ_ERROR_MESSAGE =
  "Please answer all questions before proceeding to the next step.";

const SUBMIT_QUIZ_ANSWERS = gql`
  mutation SubmitQuizAnswers(
    $courseId: ID!
    $quizId: ID!
    $responses: [QuizAnswerInput]!
    $startedOn: Float!
    $completedOn: Float
    $isAutoSave: Boolean
  ) {
    quizSubmitAnswers(
      input: {
        clientMutationId: "SubmitQuizAnswers"
        courseId: $courseId
        quizId: $quizId
        responses: $responses
        startedOn: $startedOn
        completedOn: $completedOn
        isAutoSave: $isAutoSave
      }
    ) {
      errorCode
      hasPassed
      message
      numCorrectAnswers
      numQuestions
      success
      course {
        id
        courseProgress

        quizzes {
          id
          questions {
            id
            title
            question
            isIncorrect
            answers {
              id
              title
              isMyAnswer
              isCorrect
              answerValue
              type
              essay
            }
          }
        }
      }
    }
  }
`;

const schema = yup.lazy(value => {
  const shape: Record<string, yup.AnySchema> = {};

  Object.keys(value).forEach(name => {
    if (name === CONFIRMATION_INPUT_NAME) {
      shape[name] = yup
        .boolean()
        .isTrue("Please check the box above to certify that your work is your own.");
      return;
    }

    shape[name] = yup.mixed().when("$questionTypes", questionTypes => {
      if (
        questionTypes[name] === AnswerTypeEnum.SINGLE_CHOICE ||
        questionTypes[name] === AnswerTypeEnum.LIKERT
      ) {
        return yup
          .number()
          .required(INCOMPLETE_QUIZ_ERROR_MESSAGE)
          .min(0, INCOMPLETE_QUIZ_ERROR_MESSAGE)
          .max(5, INCOMPLETE_QUIZ_ERROR_MESSAGE);
      }

      if (name === COMMENTS_ESSAY_INPUT_NAME) {
        return yup.string().optional();
      }

      // essay question
      return yup
        .string()
        .required(INCOMPLETE_QUIZ_ERROR_MESSAGE)
        .min(1, INCOMPLETE_QUIZ_ERROR_MESSAGE);
    });
  });

  return yup.object().required(INCOMPLETE_QUIZ_ERROR_MESSAGE).shape(shape);
});

interface QuizProps {
  courseUserData: CourseViewUserQueryCourse | null | undefined;
  courseId: string;
  quiz: CourseQuiz;
  hasCompleted: boolean;
  instructions: string;
  confirmationText?: string;
  currentQuiz: CourseProgressEnum;
  nextStep: CourseProgressEnum;
  isTimeExpired: boolean;
}

function formatScore(numCorrectAnswers: number, numQuestions: number) {
  const score = numCorrectAnswers / numQuestions;
  return Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(score);
}

const processAnswers = (
  courseId: string,
  quizStartTime: number,
  quiz: CourseQuiz,
  answers: [string, string | boolean][],
  isAutoSave: boolean
) => {
  let isPosttest = false;
  const responses: QuizAnswerInput[] = answers.map(([questionId, response]): QuizAnswerInput => {
    if (typeof response !== "string") {
      throw new Error(`Invalid response for question ${questionId}: ${response}`);
    }

    const question = quiz.questions.find(q => q?.id === questionId);
    const answerType = question?.answers[0]?.type;
    const answerValue = parseInt(response, 10);

    if (!question?.id) {
      throw new Error(`Invalid question: ${JSON.stringify(question)}`);
    }

    if (answerType === AnswerTypeEnum.SINGLE_CHOICE) {
      const multipleChoiceAnswers = Array.from(
        { length: question?.answers.length || 0 },
        (_, idx) => idx === answerValue
      );

      isPosttest = true;

      return {
        likertAnswer: null,
        multipleChoiceAnswers,
        questionId: question.id,
        essay: null,
      };
    }

    if (answerType === AnswerTypeEnum.LIKERT) {
      return {
        likertAnswer: answerValue,
        multipleChoiceAnswers: null,
        questionId: question.id,
        essay: null,
      };
    }

    return {
      likertAnswer: null,
      multipleChoiceAnswers: null,
      questionId: question.id,
      essay: response.toString(),
    };
  });

  const variables = {
    courseId,
    quizId: quiz.id || "",
    responses,
    startedOn: quizStartTime,
    completedOn: isAutoSave ? null : Date.now(),
    isAutoSave,
  };

  return { isPosttest, variables };
};

const AutoSave = ({
  courseId,
  quizStartTime,
  quiz,
  hasSubmitted,
  hasCompleted,
}: {
  courseId: string;
  quizStartTime: number;
  quiz: CourseQuiz;
  hasSubmitted: boolean;
  hasCompleted: boolean;
}) => {
  const formik = useFormikContext();
  const [submitQuizAnswers] = useEnhancedMutation<
    SubmitQuizAnswersMutation,
    SubmitQuizAnswersVariables
  >(SUBMIT_QUIZ_ANSWERS);

  const debouncedSetStateRef: { current: NodeJS.Timeout | null } = useRef(null);
  const variablesRef: { current: string | null } = useRef(null);

  useEffect(() => {
    if (!formik?.values || hasCompleted) return;
    if (debouncedSetStateRef.current) clearTimeout(debouncedSetStateRef.current);

    debouncedSetStateRef.current = setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { confirmed, ...answers } = formik.values as FormValues;

      const filteredAnswers = Object.entries(answers).filter(
        ([, response]) => typeof response === "string" && response !== "-1" && response !== ""
      );

      const { variables } = processAnswers(courseId, quizStartTime, quiz, filteredAnswers, true);
      const stringifiedVariables = JSON.stringify(variables);

      if (
        hasSubmitted ||
        hasCompleted ||
        !variables ||
        !variables.responses.length ||
        stringifiedVariables === variablesRef.current
      ) {
        return;
      }

      variablesRef.current = stringifiedVariables;
      submitQuizAnswers({
        variables,
      });
    }, 2000);
  }, [courseId, formik.values, hasCompleted, hasSubmitted, quiz, quizStartTime, submitQuizAnswers]);

  return null;
};

const NextStepButton = ({
  hasCompleted,
  nextStepUrl,
  isTimeExpired,
}: {
  hasCompleted: boolean;
  nextStepUrl: string;
  isTimeExpired: boolean;
}) => {
  if (hasCompleted) {
    return (
      <ButtonLink theme="primary" href={nextStepUrl}>
        Continue
      </ButtonLink>
    );
  }

  if (isTimeExpired) {
    return null;
  }

  return <SubmitButton>Submit Responses</SubmitButton>;
};

const Quiz = ({
  courseUserData,
  courseId,
  quiz,
  hasCompleted,
  instructions,
  confirmationText,
  currentQuiz,
  nextStep,
  isTimeExpired,
}: QuizProps) => {
  const { push } = useRouterNavigation();
  const { slug } = useRouterQuery<{ slug: string }>().routerQuery;
  const nextStepUrl = makeUrl(COURSE_URL, {
    slug,
    page: nextStep.toLocaleLowerCase(),
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasSubmitCompleted, setHasSubmitCompleted] = useState(false);
  const [pushNextStep, setPushNextStep] = useState<CourseProgressEnum | null>(null);

  useEffect(() => {
    if (pushNextStep && pushNextStep === courseUserData?.courseProgress) {
      push(nextStepUrl);
    }
  }, [pushNextStep, courseUserData?.courseProgress, nextStepUrl, push]);

  const [submitQuizAnswers] = useEnhancedMutation<
    SubmitQuizAnswersMutation,
    SubmitQuizAnswersVariables
  >(SUBMIT_QUIZ_ANSWERS);

  const questionGroups = useMemo(() => {
    return groupBy(quiz.questions, q => q?.title);
  }, [quiz.questions]);

  const [quizStartTime] = useState(Date.now);

  const [initialValues, questionTypes] = useMemo<[FormValues, QuestionTypes]>(() => {
    const values: FormValues = {
      [CONFIRMATION_INPUT_NAME]: !confirmationText,
    };

    const types: QuestionTypes = {};

    quiz.questions.forEach(question => {
      if (!question?.id) {
        throw new Error(`Invalid question: ${JSON.stringify(question)}`);
      }

      const questionType = question.answers[0]?.type;

      switch (questionType) {
        case AnswerTypeEnum.SINGLE_CHOICE:
          values[question.id] = question.answers.findIndex(answer => answer?.isMyAnswer).toString();
          types[question.id] = AnswerTypeEnum.SINGLE_CHOICE;
          break;
        case AnswerTypeEnum.ESSAY:
          values[question.id] = question.answers[0]?.essay || "";
          types[question.id] = AnswerTypeEnum.ESSAY;
          break;
        default:
          values[question.id] = question.answers[0]?.answerValue?.toString() || "-1";
          types[question.id] = AnswerTypeEnum.LIKERT;
          break;
      }
    });

    return [values, types];
  }, [quiz.questions, confirmationText]);

  const submitForm = useCallback(
    async (values: FormValues, { setFormSuccess, setFormError }) => {
      setHasSubmitted(true);
      setHasSubmitCompleted(false);

      if (!quiz.id) {
        setFormError("Invalid quiz");
        return;
      }

      try {
        await schema.validate(values, {
          context: { questionTypes },
        });

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { confirmed, ...answers } = values;
        const allAnswers = Object.entries(answers);
        const { isPosttest, variables } = processAnswers(
          courseId,
          quizStartTime,
          quiz,
          allAnswers,
          false
        );

        const result = await submitQuizAnswers({
          variables,
        });

        const submitResponse = result.data?.quizSubmitAnswers;

        if (!submitResponse?.success) {
          setFormError(
            "There was a problem submitting your responses. Please refresh the page and try again"
          );
        } else if ("hasPassed" in submitResponse) {
          const scoreStr = formatScore(
            submitResponse.numCorrectAnswers || -1,
            submitResponse.numQuestions || -1
          );

          if (submitResponse.hasPassed) {
            if (isPosttest) {
              setFormSuccess(
                `Great job! You have passed the posttest with a score of ${scoreStr}.`
              );
            } else {
              setFormSuccess("Success! Moving you on to the next step…");
              setPushNextStep(nextStep);
            }
          } else {
            setFormError(
              `Your score was ${scoreStr}. Sorry, you did not pass the posttest. Please try again.`
            );
          }
        }
      } catch (e) {
        setFormError(e);
      }

      setHasSubmitCompleted(true);
    },
    [courseId, nextStep, questionTypes, quiz, quizStartTime, submitQuizAnswers]
  );

  const { quizTitle, quizNumber } = getQuizTitleAndNumber(currentQuiz);

  return (
    <EnhancedFormik<FormValues>
      initialValues={initialValues}
      onSubmit={submitForm}
      resetStatusOnSubmit
    >
      <PageTitle>
        Quiz {quizNumber} of 3: {quizTitle}
      </PageTitle>
      <Form>
        <fieldset disabled={hasCompleted}>
          <p className={styles.instructions}>{instructions}</p>
          {Object.entries(questionGroups).map(([title, questions]) => {
            return (
              <div key={title} className={styles.questionSection}>
                <p className={styles.title}>{title}</p>
                <ol className={styles.questions}>
                  {questions.filter(notEmpty).map(question => {
                    return (
                      <QuizQuestion
                        key={question.id}
                        hasSubmitted={hasSubmitCompleted}
                        hasCompleted={hasCompleted}
                        question={question}
                      />
                    );
                  })}
                </ol>
              </div>
            );
          })}
          {!hasCompleted && confirmationText && (
            <div className="mb-4">
              <h2 className={styles.title}>Certify, and Submit Your Test Responses</h2>
              <div className={styles.confirmation}>
                <InputGroupField
                  name={CONFIRMATION_INPUT_NAME}
                  type="checkbox"
                  label={confirmationText}
                />
              </div>
            </div>
          )}

          <FormStatus className="mb-4" />
          <div className="flex justify-center">
            <NextStepButton
              nextStepUrl={nextStepUrl}
              hasCompleted={hasCompleted}
              isTimeExpired={isTimeExpired}
            />
          </div>
        </fieldset>
        {!isTimeExpired && (
          <AutoSave
            courseId={courseId}
            quizStartTime={quizStartTime}
            quiz={quiz}
            hasCompleted={hasCompleted}
            hasSubmitted={hasSubmitted}
          />
        )}
      </Form>
    </EnhancedFormik>
  );
};

export default Quiz;
